/**
 * @file transfer-columns.tsx
 * @description Колонки реестра перемещений для `ResponsiveList` (phase19.31,
 * зона «Склад»; разбор B — «Перемещения»).
 *
 * Состав прежний (phase19.23 этап B), «Откуда» и «Куда» — одной колонкой в
 * две строки: Номер · Откуда → куда · Основание · Статус · Создано ·
 * Позиций · Кол-во. На 1024–1279 маршрут уходит под номер, дата — под
 * статус, количество — под число позиций (`fold`). «Куда» у документа не
 * одно — целевой склад задаётся построчно, сводим к «Основной», «2 склада»,
 * «—».
 *
 * Происхождение («Быстрое действие», «Лист размещения») раньше стояло третьей
 * строкой моноширинным под номером — теперь своя колонка «Основание»; номер
 * приёмки в ней — ссылка (колонка не первая, строка-ссылка её не глотает).
 * В компактном списке вся строка — ссылка на перемещение, поэтому основание
 * там текстом.
 */

import { CreatedCell, DocumentNumber, NumberCell } from "./document-cells";
import { TransferSourceCell } from "./TransferSourceCell";
import { positionsCount, quantityTotal, totalsLine } from "./document-totals";
import type { ListColumn } from "../../ui/responsive-list-types";
import { StatusPill } from "../../ui/StatusPill";
import { TwoLineCell } from "../../ui/TwoLineCell";
import { TRANSFER_SOURCE_LABEL, type TransferListItemDto } from "../../../lib/api-transfers";
import { WMS_DOCUMENT_STATUS_META } from "../../../lib/shop/status-meta";
import { formatShopDate } from "../../../lib/shop-time";

type Transfer = TransferListItemDto;

/** «Куда» документа: один склад по имени, несколько — счётчиком. */
export function formatTransferTargets(transfer: Transfer): string {
  const names = [...new Set((transfer.items ?? []).map((item) => item.toWarehouse?.name ?? item.toWarehouseId))];
  if (names.length === 0) return "—";
  if (names.length === 1) return names[0] ?? "—";
  return `${names.length} склада`;
}

/** «Лист размещения · RCP-…» — только у не-ручных документов. */
export function transferSourceNote(transfer: Transfer): string | null {
  if (transfer.source === "MANUAL") return null;
  const label = TRANSFER_SOURCE_LABEL[transfer.source];
  return transfer.receipt ? `${label} · ${transfer.receipt.receiptNumber}` : label;
}

/** «Основной → Витрина» — маршрут одной строкой. */
function routeLine(t: Transfer): string {
  return `${t.fromWarehouse?.name ?? "—"} → ${formatTransferTargets(t)}`;
}

/** Колонки; `receiptsPath` — база адреса приёмок для ссылки на основание. */
export function transferColumns(receiptsPath: string): ListColumn<Transfer>[] {
  return [
    {
      key: "number",
      header: "Номер",
      width: "w-[168px]",
      cell: (t) => <DocumentNumber value={t.transferNumber} />,
      compactCell: (t) => <span className="font-mono">{t.transferNumber}</span>,
      compact: "title",
    },
    {
      key: "route",
      header: "Откуда → куда",
      width: "w-[176px]",
      cell: (t) => <TwoLineCell primary={t.fromWarehouse?.name ?? "—"} secondary={`→ ${formatTransferTargets(t)}`} />,
      compact: "subtitle",
      compactCell: routeLine,
      fold: { into: "number", cell: routeLine },
    },
    {
      key: "source",
      header: "Основание",
      cell: (t) => <TransferSourceCell transfer={t} receiptsPath={receiptsPath} />,
      compact: "meta",
      compactCell: (t) => transferSourceNote(t),
    },
    {
      key: "status",
      header: "Статус",
      width: "w-[132px]",
      cell: (t) => <StatusPill meta={WMS_DOCUMENT_STATUS_META[t.status]} />,
      compact: "trailing",
    },
    {
      key: "created",
      header: "Создано",
      width: "w-[148px]",
      cell: (t) => <CreatedCell at={t.createdAt} by={t.createdBy?.displayName} />,
      fold: { into: "status", cell: (t) => formatShopDate(t.createdAt) },
    },
    {
      key: "positions",
      header: "Позиций",
      width: "w-[88px]",
      align: "end",
      cell: (t) => <NumberCell value={positionsCount(t.items)} />,
      compact: "meta",
      compactCell: (t) => totalsLine(t.items),
    },
    {
      key: "quantity",
      header: "Кол-во",
      width: "w-[88px]",
      align: "end",
      cell: (t) => <NumberCell value={quantityTotal(t.items)} />,
      fold: { into: "positions", cell: (t) => `${quantityTotal(t.items)} шт` },
    },
    { key: "date", header: "Дата", compactOnly: true, cell: (t) => formatShopDate(t.createdAt), compact: "meta" },
  ];
}
